import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { PackageCoverageLimitModel, PackageCoverageLimitType } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { ChipComponent } from './chip.component';
import { ChipTone } from './guarantee-vocabulary';

interface LimitPresentation {
  icon: string;
  tone: ChipTone;
  labelKey: string;
  valueKey: string;
  hintKey: string;
}

/** Ordered as a manager reads them: how much is covered, how many times, and what the member pays. */
const LIMITS: Record<PackageCoverageLimitType, LimitPresentation> = {
  [PackageCoverageLimitType.Price]: {
    icon: 'payments',
    tone: 'primary',
    labelKey: 'guaranteeV2.label.limitPriceLabel',
    valueKey: 'guaranteeV2.label.limitPrice',
    hintKey: 'guaranteeV2.message.limitPriceHint',
  },
  [PackageCoverageLimitType.Quantity]: {
    icon: 'tag',
    tone: 'info',
    labelKey: 'guaranteeV2.label.limitQuantityLabel',
    valueKey: 'guaranteeV2.label.limitQuantity',
    hintKey: 'guaranteeV2.message.limitQuantityHint',
  },
  [PackageCoverageLimitType.Franchise]: {
    icon: 'percent',
    tone: 'warning',
    labelKey: 'guaranteeV2.label.limitFranchiseLabel',
    valueKey: 'guaranteeV2.label.limitFranchise',
    hintKey: 'guaranteeV2.message.limitFranchiseHint',
  },
};

const ORDER = [PackageCoverageLimitType.Price, PackageCoverageLimitType.Quantity, PackageCoverageLimitType.Franchise];

/** A package option's or sub-option's limits, each named by its type and explained on hover. */
@Component({
  selector: 'guarantees-package-limits',
  standalone: true,
  imports: [DecimalPipe, TranslatePipe, ChipComponent],
  host: { class: 'flex flex-wrap gap-1' },
  template: `
    @for (limit of sortedLimits(); track $index) {
      @let view = presentation[limit.type];
      <guarantees-chip [tone]="view.tone" [icon]="view.icon" [tooltip]="view.hintKey | translate">
        <span class="font-normal opacity-80">{{ view.labelKey | translate }}</span>
        {{ view.valueKey | translate: { amount: (limit.amount | number: '1.0-2') } }}
      </guarantees-chip>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PackageLimitsComponent {
  limits = input<PackageCoverageLimitModel[] | undefined>([]);

  protected readonly presentation = LIMITS;

  protected readonly sortedLimits = computed(() =>
    (this.limits() ?? []).filter(limit => LIMITS[limit.type]).sort((first, second) => ORDER.indexOf(first.type) - ORDER.indexOf(second.type))
  );
}
